import { NextRouter } from 'next/router';
import React from 'react';

import {
  GetMenusResponse,
  GetNavListResponse,
  GetRecentCommendResponse,
} from '@/request/interface/resources';

import { DEFAULT_COVER_MAP } from './constants';
import { ResourceType } from './types';

export type Paths = {
  href?: string;
  hrefEn?: string;
  target?: string;
  label: string;
  ga?: string;
  children?: SelectorChildrenItem[];
}[];

export interface SelectorChildrenItem {
  label: string;
  href: string;
  hrefEn?: string;
  ga?: string;
  target?: string;
  // 自研埋点 下拉列表组名称
  traceEventGroupName?: string;
  children?: SelectorChildrenItem[];
}

export interface TreeNode {
  label: string;
  href: string;
  children?: TreeNode[];
}

export interface Menu {
  label: string;
  value: string;
  subMenu?: Menu[];
  parentValue?: string;
}

/**
 * 查找某个节点，返回该节点的路径
 *
 * @param {TreeNode[]} tree 树
 * @param {string} targetHref 需要查找的属性
 * @param {string[]} [currentPath=[]] 当前路径
 * @return {*}  {(TreeNode[] | null)}
 */
export const findNodeWithPath = (
  tree: TreeNode[],
  targetHref: string,
  currentPath: TreeNode[] = [],
): TreeNode[] | null => {
  if (targetHref === '/') {
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    const path = [...currentPath, node];

    if (node.href === targetHref) {
      return path;
    }

    if (node.children) {
      const result = findNodeWithPath(node.children, targetHref, path);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

const findMenu = (value: string, data: Menu[]): Menu | null => {
  // eslint-disable-next-line no-restricted-syntax
  for (const item of data) {
    if (item.value === value) {
      return item;
    }
  }

  return null;
};

/**
 * 从菜单数据中提取所有可能的标签值
 *
 * @param {Menu[]} menuData
 * @return {*}  {string[]}
 */
export const extractAllTagValues = (menuData: Menu[]): string[] => {
  const tagValues: string[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const menu of menuData) {
    if (menu.subMenu) {
      // eslint-disable-next-line no-restricted-syntax
      for (const subMenu of menu.subMenu) {
        tagValues.push(subMenu.value);
      }
    }
  }

  return tagValues;
};

/**
 * 智能分词算法 - 基于已知标签列表进行匹配
 *
 * @param {string} inputString
 * @param {string[]} knownTags
 * @return {*}  {string[]}
 */
export const smartParseTags = (
  inputString: string,
  knownTags: string[],
): string[] => {
  if (!inputString || !knownTags.length) {
    return [];
  }

  // 1. 按连字符分割
  const parts = inputString.split('-');
  const result: string[] = [];

  // 2. 使用动态规划匹配
  let i = 0;
  while (i < parts.length) {
    let current = '';
    let matched = false;

    // 从当前位置开始尝试匹配
    for (let j = i; j < parts.length; j++) {
      current += (current ? '-' : '') + parts[j];

      // 检查是否匹配已知标签
      if (knownTags.includes(current)) {
        result.push(current);
        matched = true;
        i = j + 1;
        break;
      }
    }

    if (!matched) {
      // 没有找到匹配，将当前位置的部分作为一个单位处理
      const fallbackPart = parts[i];
      result.push(fallbackPart);
      i += 1;
    }
  }

  return result;
};

/**
 * 根据字符串查找节点
 *
 * @param {Menu[]} data
 * @param {string} inputString
 * @return {*}  {Menu[]}
 */
export const findMenusByString = (
  inputString: string,
  data: Menu[],
): Menu[] => {
  if (!inputString) {
    return [];
  }

  // 提取所有已知标签值
  const knownTags = extractAllTagValues(data);

  // 使用智能分词
  const values = smartParseTags(inputString, knownTags);

  const result: Menu[] = [];
  let menus: Menu[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const item of data) {
    if (item?.subMenu?.length) {
      menus = [...menus, ...item.subMenu];
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const value of values) {
    const menu = findMenu(value, menus);
    if (menu) {
      result.push(menu);
    }
  }

  return result;
};

/**
 * 根据资源类型获取 icon text color
 *
 * @param {(name: string) => string} t
 * @param {(ResourceType)} type
 * @return {*}
 */
export const getResourceTypeContent = (
  t: (name: string) => string,
  type: ResourceType,
) => {
  const map = {
    document: {
      color: '#FF5353',
      text: t('文档'),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10.001 1.59985V3.99985C10.001 4.44168 10.3592 4.79985 10.801 4.79985H13.201M5.60103 4.79985H7.20103M5.60103 7.19985H10.401M5.60103 9.59985H10.401M12.001 2.79985C11.645 2.48128 11.2755 2.10344 11.0423 1.85804C10.8871 1.69474 10.6726 1.59985 10.4473 1.59985H4.40084C3.51719 1.59985 2.80085 2.31619 2.80084 3.19984L2.80078 12.7998C2.80078 13.6835 3.51712 14.3998 4.40077 14.3998L11.6008 14.3998C12.4844 14.3999 13.2008 13.6835 13.2008 12.7999L13.201 4.31841C13.201 4.11385 13.123 3.91721 12.981 3.76994C12.7185 3.49762 12.2801 3.04953 12.001 2.79985Z"
            stroke="#FF5353"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    video: {
      color: '#FF5353',
      text: t('视频'),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M10.3973 8.53947L10.0336 7.98076L10.0243 7.98681L10.0152 7.99317L10.3973 8.53947ZM10.3973 7.62202L10.002 8.15885L10.0317 8.18075L10.0637 8.19923L10.3973 7.62202ZM6.72034 4.9145L7.11563 4.37767L7.09682 4.36382L7.07709 4.35131L6.72034 4.9145ZM5.94949 5.3189L6.61626 5.31697L6.61602 5.3053L5.94949 5.3189ZM5.96491 10.6374L5.29824 10.6393L5.2983 10.6582L5.29942 10.677L5.96491 10.6374ZM6.76402 11.0806L7.08683 11.6639L7.11744 11.6469L7.14611 11.6269L6.76402 11.0806ZM3.99766 2.26652H11.9977V0.933187H3.99766V2.26652ZM13.731 3.99985V11.9999H15.0643V3.99985H13.731ZM11.9977 13.7332H3.99766V15.0665H11.9977V13.7332ZM2.26432 11.9999V3.99985H0.93099V11.9999H2.26432ZM3.99766 13.7332C3.04036 13.7332 2.26432 12.9571 2.26432 11.9999H0.93099C0.93099 13.6935 2.30398 15.0665 3.99766 15.0665V13.7332ZM13.731 11.9999C13.731 12.9571 12.9549 13.7332 11.9977 13.7332V15.0665C13.6913 15.0665 15.0643 13.6935 15.0643 11.9999H13.731ZM11.9977 2.26652C12.9549 2.26652 13.731 3.04256 13.731 3.99985H15.0643C15.0643 2.30618 13.6913 0.933187 11.9977 0.933187V2.26652ZM3.99766 0.933187C2.30398 0.933187 0.93099 2.30618 0.93099 3.99985H2.26432C2.26432 3.04256 3.04036 2.26652 3.99766 2.26652V0.933187ZM10.761 9.09818C11.0506 8.90967 11.4167 8.57341 11.416 8.06658C11.4153 7.5448 11.0304 7.21794 10.7309 7.04481L10.0637 8.19923C10.1335 8.23955 10.1463 8.26053 10.1366 8.24815C10.1222 8.2298 10.0828 8.16662 10.0826 8.0684C10.0825 7.97263 10.1199 7.91626 10.1257 7.90837C10.1286 7.90455 10.1081 7.93227 10.0336 7.98076L10.761 9.09818ZM10.7926 7.08519L7.11563 4.37767L6.32504 5.45133L10.002 8.15885L10.7926 7.08519ZM7.07709 4.35131C6.76335 4.15257 6.32276 4.03871 5.90609 4.23326C5.45259 4.445 5.27416 4.90115 5.28296 5.3325L6.61602 5.3053C6.61522 5.26626 6.62335 5.27888 6.60284 5.31811C6.57974 5.36231 6.53461 5.4113 6.47018 5.44139C6.34621 5.49927 6.29619 5.43498 6.36359 5.47768L7.07709 4.35131ZM5.28283 5.32083L5.29824 10.6393L6.63157 10.6354L6.61616 5.31697L5.28283 5.32083ZM5.29942 10.677C5.32063 11.0332 5.44446 11.5081 5.89141 11.7468C6.33148 11.9818 6.78964 11.8283 7.08683 11.6639L6.44121 10.4973C6.37513 10.5338 6.34915 10.5359 6.35959 10.5344C6.37536 10.5323 6.44047 10.5284 6.51954 10.5707C6.59973 10.6135 6.63444 10.6717 6.64285 10.6896C6.64839 10.7014 6.63521 10.6786 6.6304 10.5978L5.29942 10.677ZM7.14611 11.6269L10.7794 9.08578L10.0152 7.99317L6.38193 10.5343L7.14611 11.6269Z"
            fill="#FF5353"
          />
        </svg>
      ),
    },
    audio: {
      color: '#FF5353',
      text: t('音频'),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.4607 5.13947C14.5869 6.97402 14.746 9.41572 13.6747 11.5764M11.0881 6.33942C12.0387 7.47945 12.1729 8.99676 11.2687 10.3394M7.99766 3.62231L4.86006 6.16748H1.59766V9.83208L4.86006 9.83117L7.99766 12.3776V3.62231Z"
            stroke="#FF5353"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    other: {
      color: '#484C70',
      text: t('其它'),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none">
          <path
            d="M5.29688 7H9.79688"
            stroke="#484C70"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5.29688 9.80005H10.6969"
            stroke="#484C70"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M2.10156 3.5C2.10156 2.39543 2.99699 1.5 4.10156 1.5H10C10.2761 1.5 10.5 1.72386 10.5 2V4.2C10.5 4.64183 10.8582 5 11.3 5H13.4016C13.6777 5 13.9016 5.22386 13.9016 5.5V8V12.5C13.9016 13.6046 13.0061 14.5 11.9016 14.5H4.10156C2.997 14.5 2.10156 13.6046 2.10156 12.5V3.5Z"
            stroke="#484C70"
          />
          <path
            d="M10.2 1.5L13.7355 5.03553"
            stroke="#484C70"
            stroke-linecap="round"
          />
        </svg>
      ),
    },
  };
  return map[type || 'document'];
};

/**
 * 获取详情页按钮图标与文案
 *
 * @param {(name: string) => string} t
 * @param {(ResourceType)} type
 * @return {*}
 */
export const getPreviewSourceContent = (
  t: (name: string) => string,
  type: ResourceType,
  noDownloadAuth: boolean,
  allowedDown?: boolean,
) => {
  const map = {
    video: {
      text: t('播放'),
      icon: (
        <svg
          width="21"
          height="20"
          viewBox="0 0 21 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.4994 10.6745L13.0448 9.97614L13.0332 9.9837L13.0218 9.99164L13.4994 10.6745ZM13.4994 9.52771L13.0053 10.1987L13.0425 10.2261L13.0825 10.2492L13.4994 9.52771ZM8.90325 6.1433L9.39737 5.47227L9.37386 5.45495L9.34919 5.43933L8.90325 6.1433ZM7.93969 6.64881L8.77303 6.64639L8.773 6.6391L8.77285 6.6318L7.93969 6.64881ZM7.95897 13.2969L7.12564 13.2993L7.1257 13.3229L7.12711 13.3464L7.95897 13.2969ZM8.95786 13.8509L9.36137 14.58L9.39963 14.5589L9.43547 14.5338L8.95786 13.8509ZM10.5 17.1667C6.54196 17.1667 3.33333 13.958 3.33333 10H1.66667C1.66667 14.8785 5.62148 18.8333 10.5 18.8333V17.1667ZM17.6667 10C17.6667 13.958 14.458 17.1667 10.5 17.1667V18.8333C15.3785 18.8333 19.3333 14.8785 19.3333 10H17.6667ZM10.5 2.83333C14.458 2.83333 17.6667 6.04196 17.6667 10H19.3333C19.3333 5.12148 15.3785 1.16667 10.5 1.16667V2.83333ZM10.5 1.16667C5.62148 1.16667 1.66667 5.12148 1.66667 10H3.33333C3.33333 6.04196 6.54196 2.83333 10.5 2.83333V1.16667ZM13.9541 11.3729C14.3161 11.1373 14.7736 10.7169 14.7728 10.0834C14.7719 9.43118 14.2909 9.02261 13.9164 8.8062L13.0825 10.2492C13.1697 10.2996 13.1857 10.3258 13.1736 10.3104C13.1556 10.2874 13.1063 10.2085 13.1061 10.0857C13.106 9.96597 13.1527 9.89551 13.16 9.88564C13.1635 9.88087 13.1379 9.91552 13.0448 9.97614L13.9541 11.3729ZM13.9935 8.85667L9.39737 5.47227L8.40913 6.81434L13.0053 10.1987L13.9935 8.85667ZM9.34919 5.43933C8.95702 5.1909 8.40629 5.04857 7.88544 5.29176C7.31856 5.55644 7.09553 6.12663 7.10654 6.66581L8.77285 6.6318C8.77186 6.583 8.78201 6.59878 8.75639 6.64782C8.72751 6.70308 8.6711 6.76431 8.59056 6.80192C8.43559 6.87427 8.37307 6.79391 8.45732 6.84728L9.34919 5.43933ZM7.10637 6.65122L7.12564 13.2993L8.7923 13.2945L8.77303 6.64639L7.10637 6.65122ZM7.12711 13.3464C7.15361 13.7917 7.3084 14.3853 7.8671 14.6837C8.41718 14.9774 8.98989 14.7856 9.36137 14.58L8.55434 13.1218C8.47174 13.1675 8.43927 13.17 8.45232 13.1682C8.47203 13.1655 8.55341 13.1607 8.65226 13.2135C8.75249 13.2671 8.79588 13.3398 8.80639 13.3622C8.81332 13.377 8.79684 13.3484 8.79083 13.2474L7.12711 13.3464ZM9.43547 14.5338L13.977 11.3574L13.0218 9.99164L8.48024 13.168L9.43547 14.5338Z"
            fill="white"
          />
        </svg>
      ),
    },
    audio: {
      text: t('播放'),
      icon: (
        <svg
          width="21"
          height="20"
          viewBox="0 0 21 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.4994 10.6745L13.0448 9.97614L13.0332 9.9837L13.0218 9.99164L13.4994 10.6745ZM13.4994 9.52771L13.0053 10.1987L13.0425 10.2261L13.0825 10.2492L13.4994 9.52771ZM8.90325 6.1433L9.39737 5.47227L9.37386 5.45495L9.34919 5.43933L8.90325 6.1433ZM7.93969 6.64881L8.77303 6.64639L8.773 6.6391L8.77285 6.6318L7.93969 6.64881ZM7.95897 13.2969L7.12564 13.2993L7.1257 13.3229L7.12711 13.3464L7.95897 13.2969ZM8.95786 13.8509L9.36137 14.58L9.39963 14.5589L9.43547 14.5338L8.95786 13.8509ZM10.5 17.1667C6.54196 17.1667 3.33333 13.958 3.33333 10H1.66667C1.66667 14.8785 5.62148 18.8333 10.5 18.8333V17.1667ZM17.6667 10C17.6667 13.958 14.458 17.1667 10.5 17.1667V18.8333C15.3785 18.8333 19.3333 14.8785 19.3333 10H17.6667ZM10.5 2.83333C14.458 2.83333 17.6667 6.04196 17.6667 10H19.3333C19.3333 5.12148 15.3785 1.16667 10.5 1.16667V2.83333ZM10.5 1.16667C5.62148 1.16667 1.66667 5.12148 1.66667 10H3.33333C3.33333 6.04196 6.54196 2.83333 10.5 2.83333V1.16667ZM13.9541 11.3729C14.3161 11.1373 14.7736 10.7169 14.7728 10.0834C14.7719 9.43118 14.2909 9.02261 13.9164 8.8062L13.0825 10.2492C13.1697 10.2996 13.1857 10.3258 13.1736 10.3104C13.1556 10.2874 13.1063 10.2085 13.1061 10.0857C13.106 9.96597 13.1527 9.89551 13.16 9.88564C13.1635 9.88087 13.1379 9.91552 13.0448 9.97614L13.9541 11.3729ZM13.9935 8.85667L9.39737 5.47227L8.40913 6.81434L13.0053 10.1987L13.9935 8.85667ZM9.34919 5.43933C8.95702 5.1909 8.40629 5.04857 7.88544 5.29176C7.31856 5.55644 7.09553 6.12663 7.10654 6.66581L8.77285 6.6318C8.77186 6.583 8.78201 6.59878 8.75639 6.64782C8.72751 6.70308 8.6711 6.76431 8.59056 6.80192C8.43559 6.87427 8.37307 6.79391 8.45732 6.84728L9.34919 5.43933ZM7.10637 6.65122L7.12564 13.2993L8.7923 13.2945L8.77303 6.64639L7.10637 6.65122ZM7.12711 13.3464C7.15361 13.7917 7.3084 14.3853 7.8671 14.6837C8.41718 14.9774 8.98989 14.7856 9.36137 14.58L8.55434 13.1218C8.47174 13.1675 8.43927 13.17 8.45232 13.1682C8.47203 13.1655 8.55341 13.1607 8.65226 13.2135C8.75249 13.2671 8.79588 13.3398 8.80639 13.3622C8.81332 13.377 8.79684 13.3484 8.79083 13.2474L7.12711 13.3464ZM9.43547 14.5338L13.977 11.3574L13.0218 9.99164L8.48024 13.168L9.43547 14.5338Z"
            fill="white"
          />
        </svg>
      ),
    },
    document: {
      text: allowedDown ? t('查看和下载') : t('查看'),
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none">
          <path
            d="M3 12.6832L3 15.7714C3 16.2395 3.18437 16.6883 3.51256 17.0193C3.84075 17.3502 4.28587 17.5361 4.75 17.5361H15.25C15.7141 17.5361 16.1592 17.3502 16.4874 17.0193C16.8156 16.6883 17 16.2395 17 15.7714V12.6832M10.001 2.46387V12.4639M10.001 12.4639L14.001 8.64291M10.001 12.4639L6.00098 8.64291"
            stroke="white"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    other: {
      text: allowedDown ? t('下载') : t('预览'), // TODO: @lxd 需要翻译
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none">
          <path
            d="M3 12.6832L3 15.7714C3 16.2395 3.18437 16.6883 3.51256 17.0193C3.84075 17.3502 4.28587 17.5361 4.75 17.5361H15.25C15.7141 17.5361 16.1592 17.3502 16.4874 17.0193C16.8156 16.6883 17 16.2395 17 15.7714V12.6832M10.001 2.46387V12.4639M10.001 12.4639L14.001 8.64291M10.001 12.4639L6.00098 8.64291"
            stroke="white"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  };
  const content = map[type || 'document'];
  if (noDownloadAuth) {
    content.icon = (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M5.5 7.33333V6.57143C5.5 4.039 7.50714 2 10 2C12.4929 2 14.5 4.039 14.5 6.57143V7.33333M5.5 7.33333C4.675 7.33333 4 8.01905 4 8.85714V16.4762C4 17.3143 4.675 18 5.5 18H14.5C15.325 18 16 17.3143 16 16.4762V8.85714C16 8.01905 15.325 7.33333 14.5 7.33333M5.5 7.33333H14.5M10 13.5V11.5"
          stroke="white"
          strokeWidth="1.66667"
          strokeLinecap="round"
        />
      </svg>
    );
  } else if (!allowedDown) {
    content.icon = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none">
        <path
          d="M10 8.125C10.3708 8.125 10.7334 8.23497 11.0417 8.44099C11.35 8.64702 11.5904 8.93986 11.7323 9.28247C11.8742 9.62508 11.9113 10.0021 11.839 10.3658C11.7666 10.7295 11.5881 11.0636 11.3258 11.3258C11.0636 11.588 10.7295 11.7666 10.3658 11.839C10.0021 11.9113 9.62508 11.8742 9.28247 11.7323C8.93986 11.5904 8.64702 11.35 8.441 11.0417C8.23497 10.7334 8.125 10.3708 8.125 10C8.125 9.50272 8.32255 9.0258 8.67418 8.67417C9.02581 8.32254 9.50272 8.125 10 8.125ZM10 6.875C9.38193 6.875 8.77775 7.05828 8.26384 7.40166C7.74994 7.74504 7.3494 8.2331 7.11288 8.80411C6.87635 9.37513 6.81447 10.0035 6.93505 10.6097C7.05563 11.2158 7.35325 11.7727 7.79029 12.2097C8.22733 12.6467 8.78415 12.9444 9.39034 13.065C9.99654 13.1855 10.6249 13.1236 11.1959 12.8871C11.7669 12.6506 12.255 12.2501 12.5983 11.7362C12.9417 11.2223 13.125 10.6181 13.125 10C13.125 9.1712 12.7958 8.37634 12.2097 7.79029C11.6237 7.20424 10.8288 6.875 10 6.875Z"
          fill="white"
        />
        <path
          d="M10 5.83789C13.6844 5.83789 15.625 8.06289 17.1625 10.0004C15.625 11.941 13.6844 14.1629 10 14.1629C6.31563 14.1629 4.375 11.941 2.8375 10.0004C4.375 8.05977 6.31563 5.83789 10 5.83789ZM10 4.58789C5.16875 4.58789 2.88125 7.94102 1.25 10.0004C2.88125 12.0598 5.16875 15.4129 10 15.4129C14.8313 15.4129 17.1187 12.0598 18.75 10.0004C17.1187 7.94102 14.8313 4.58789 10 4.58789Z"
          fill="white"
        />
      </svg>
    );
  }
  return content;
};

/**
 * 构建首页导航菜单
 *
 * @param {GetNavListResponse['content']} data
 * @return {*}
 */
export const generateNavList = (
  data: GetNavListResponse['content'],
  isMobile?: boolean,
) => {
  const navList = Object.entries(data || {});
  if (!navList.length) {
    return [];
  }
  const list: Paths = [];
  const subjectMap = {
    数学: 'math',
    中文: 'chinese',
    英文: 'english',
    Math: 'math',
    Chinese: 'chinese',
    English: 'english',
  };
  const labelMap = {
    数学: '国际数学',
    中文: '国际中文',
    英文: '英文读写',
    Math: 'Math',
    Chinese: 'Chinese',
    English: isMobile ? 'Language Arts' : 'English Language Arts',
  };
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < navList.length; i++) {
    const [key, value] = navList[i];
    if (['中文', '数学', '英文', 'Chinese', 'Math', 'English'].includes(key)) {
      const routeSubject =
        subjectMap[
          key as '中文' | '数学' | '英文' | 'Chinese' | 'Math' | 'English'
        ];
      list.push({
        label:
          labelMap[
            key as '中文' | '数学' | '英文' | 'Chinese' | 'Math' | 'English'
          ],
        href: `/resources/${routeSubject}/`,
        children: value.map(({ name, routeName }) => ({
          label: name,
          href: `/resources/${routeSubject}/${routeName}/`,
        })),
      });
    }
    // if (['专题', 'Topic'].includes(key)) {
    //   list.push({
    //     label: key,
    //     href: '/resources/',
    //     children: value.map(({ name, routeSubject, uuid }) => ({
    //       label: name,
    //       href: `/resources/${routeSubject?.toLocaleLowerCase()}/topic/${uuid}/`,
    //     })),
    //   });
    // }
  }

  return list;
};

/**
 * 转换资源列表数据
 *
 * @param {GetRecentCommendResponse['content']} data
 * @param {Language} lang
 * @return {*}
 */
export const convertReSourceList = (
  data: GetRecentCommendResponse['content'],
) => {
  if (!data?.length) {
    return [];
  }
  return data.map(
    ({
      uuid,
      coverPic,
      name,
      themeName,
      iconType,
      category,
      subject,
      subjectDisplay,
      summary,
      resourceLevel,
      ...rest
    }) => ({
      title: name,
      subjectLabel: subjectDisplay,
      subjectValue: subject?.toLowerCase?.(),
      abstract: summary,
      label: iconType?.toLowerCase?.() as 'hot' | 'free',
      background: coverPic || DEFAULT_COVER_MAP[category as ResourceType],
      resourceType: category,
      tag: themeName,
      id: uuid,
      resourceLevel,
      ...rest,
    }),
  );
};

/**
 * 通过路由获取资源详情页id
 *
 * @param {NextRouter} router
 */
export const getDetailId = (router: NextRouter) => {
  const { id: idInfo } = router.query;
  const idList = (idInfo as string)?.split?.('_') || [];
  let id = idList?.length ? idList[idList.length - 1] : '';
  id = id.replace('detail', '');
  return id;
};

/**
 * 通过路由获取资源详情页seo信息
 *
 * @param {NextRouter} router
 */
export const getSeoUrl = (router: NextRouter) => {
  const { id: idInfo } = router.query;
  const idList = (idInfo as string)?.split?.('_') || [];
  return idList?.length ? idList[0] : '';
};

/**
 * 转换分类页左侧按钮数据
 * @param data
 * @returns
 */
export const getConvertMenuData = (data: GetMenusResponse['content']) => {
  if (!data?.length) {
    return [];
  }
  const result = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < data.length; i++) {
    const { key, list, displayType } = data[i];
    const newSubMenu = list?.map(item => {
      const { name, routeName, uuid, tagType, ...rest } = item;
      return {
        ...rest,
        label: name,
        value: routeName,
        id: uuid,
        menuType: tagType,
        parentValue: key,
        parentOrder: i + 1,
      };
    });
    result.push({
      label: key,
      value: key,
      subMenu: newSubMenu,
      displayType,
    });
  }
  return result;
};
